import {
  useReducer,
  useEffect,
  useRef,
  useContext,
  useState,
  useCallback,
} from 'react';

import { cloneDeep } from '@apollo/client/utilities';

import { setHeader } from 'lane-shared/apollo/index';
import { InitializeChannelsResult } from 'lane-shared/graphql/query/initializeChannels';
import { HEADER_PRIMARY_CHANNEL_ID } from 'constants-activate';

import { AnalyticsContext } from 'lane-shared/contexts';
import {
  emptyResults,
  defaultContext,
  ChannelsContextType,
} from 'lane-shared/contexts/ChannelsContext';
import { initializeChannels } from 'lane-shared/graphql/query';
import {
  switchChannelQuery,
  SwitchChannelResult,
} from 'lane-shared/hooks/useUserChannelsDataQuery';
import { isNotLoggedInError } from 'lane-shared/helpers';
import Storage, { USER_CHANNELS_DATA_CONTEXT } from 'lane-shared/helpers/Storage';
import emitter, {
  EVENT_SUBSCRIPTIONS_CHANGED,
  EVENT_AUTH_TOKEN,
  EVENT_AUTH_TOKEN_INVALID,
  EVENT_INVALID_HOME_CHANNEL,
} from 'lane-shared/helpers/emitter';
import { processFocusOnChannels } from 'lane-shared/helpers/user';
import { UserType } from 'lane-shared/types/User';
import useFlag from 'lane-shared/hooks/useFlag';
import { FeatureFlag } from 'constants-flags';
import { useMultiLanguage } from 'lane-shared/hooks/useMultiLanguage';
import usePollingQuery from 'lane-shared/hooks/usePollingQuery';
import { useAuth0Enabled } from 'lane-shared/hooks/auth0/useAuth0Enabled';
import { Channel } from 'lane-shared/types/ChannelType';
import { useBestDefaultChannel } from './useBestDefaultChannel';

let instances = 0;

type SwitchFn = (channelId: string, forAdmin: boolean) => void;
type UpdateStateFn = (props: Partial<ChannelsContextType>) => void;


function applyHomeChannelPriority(
  bestDefaultChannel: Channel | null,
  homeChannelApplied: boolean,
  forAdmin: boolean,
  switchChannel: SwitchFn
): boolean {
  if (bestDefaultChannel && !homeChannelApplied) {
    switchChannel(bestDefaultChannel._id, forAdmin);

    return true;
  }

  return false;
}

function handleNeedsDefaultChannel(
  bestDefaultChannel: Channel | null,
  forAdmin: boolean,
  switchChannel: SwitchFn,
  updateState: UpdateStateFn
): void {
  if (bestDefaultChannel === null) {
    updateState({ primaryChannelInitialized: false, isReady: false });

    return;
  }

  switchChannel(bestDefaultChannel._id, forAdmin);
}

function applyFallbackRouting(params: {
  channels: Channel[];
  primaryId: string | null;
  primaryChannelId: string | undefined;
  channelId: string | undefined | null;
  forAdmin: boolean;
  bestDefaultChannel: Channel | null;
  switchChannel: SwitchFn;
  updateState: UpdateStateFn;
}): void {
  const { channels, primaryId, primaryChannelId, channelId, forAdmin, bestDefaultChannel, switchChannel, updateState } = params;
  const hasValidPrimaryChannel = primaryId && channels.some(({ _id }) => _id === primaryId);
  const needsDefaultChannel = !hasValidPrimaryChannel && !channelId;

  if (needsDefaultChannel) {
    handleNeedsDefaultChannel(bestDefaultChannel, forAdmin, switchChannel, updateState);

    return;
  }

  if (channelId && channelId !== primaryChannelId) {
    switchChannel(channelId, forAdmin);

    return;
  }

  if (primaryChannelId !== primaryId) {
    const storedChannel = channels.find(channel => channel._id === primaryId);

    if (storedChannel) {
      switchChannel(storedChannel._id, forAdmin);
    }
  }
}

type Data = {
  primaryId: string | null;
  secondaryId: string | null;
  forAdmin: boolean;
};

type Props = {
  user: UserType | null;
  channelId?: string | null;
  isDeepLink?: boolean;
  defaultChannelId?: string | null;
  onFocusChannelChanged?: (
    primaryChannel: Channel | undefined,
    secondaryChannel: Channel | undefined
  ) => void;
};

export function useUserChannelsData({
  user,
  channelId,
  isDeepLink,
  defaultChannelId: defaultChannelIdFromUser,
  onFocusChannelChanged = () => undefined,
}: Props) {
  const isAuth0Enabled = useAuth0Enabled();

  const [isPostLogin, setIsPostLogin] = useState(false);
  const isPostLoginRef = useRef(false);

  const statusRef = useRef<{
    isSwitching: boolean;
    isMounted: boolean;
    forAdmin: boolean;
    channelId: string | null;
    primaryId: string | null;
    secondaryId: string | null;
    storageInitialized: boolean;
    userInitialized: boolean;
    homeChannelApplied: boolean;
    pollingStarted: boolean;
    showSecondaryPages: boolean;
    lastSeenDefaultChannelId: string | null | undefined;
    isHomeChannelSelectionEnabled: boolean;
  }>({
    isSwitching: false,
    forAdmin: false,
    isMounted: false,
    channelId: null,
    primaryId: null,
    secondaryId: null,
    storageInitialized: false,
    userInitialized: false,
    homeChannelApplied: false,
    pollingStarted: false,
    showSecondaryPages: true,
    lastSeenDefaultChannelId: undefined,
    isHomeChannelSelectionEnabled: false,
  });

  const [userChannels, updateState] = useReducer(
    (state: ChannelsContextType, props: Partial<ChannelsContextType>) => ({ ...state, ...props }),
    { ...defaultContext }
  );

  const markPostLoginRoutingResolved = useCallback(() => {
    setIsPostLogin(false);
  }, []);

  const bestDefaultChannel = useBestDefaultChannel(
    userChannels.channels,
    undefined,
    defaultChannelIdFromUser,
    {
      isDeepLink,
      isPostLogin,
      onPostLoginRoutingResolved: markPostLoginRoutingResolved,
    }
  );

  const showSecondaryPages = useFlag(FeatureFlag.ConsumerExperienceTM8806HideChannelTabsFromBuilding, true) ?? true;
  const isHomeChannelSelectionEnabled = useFlag(FeatureFlag.UserHomeChannelSelection, false) ?? false;
  const isMemberInteractionUpdateEnabled = useFlag(FeatureFlag.MemberInteractionUpdate, false);

  useEffect(() => {
    statusRef.current.showSecondaryPages = showSecondaryPages;
  }, [showSecondaryPages]);

  useEffect(() => {
    statusRef.current.isHomeChannelSelectionEnabled = isHomeChannelSelectionEnabled;
  }, [isHomeChannelSelectionEnabled]);

  const { translate } = useMultiLanguage({ user });

  const analytics = useContext(AnalyticsContext);

  const channelsQuery = usePollingQuery<InitializeChannelsResult>({
    query: initializeChannels,
    onAfterFetch: data => {
      const channels = data?.me?.channels || emptyResults;

      updateState({
        channelsInitialized: true,
        channels,
      });
    },
    onError: error => {
      updateState({
        channelsInitialized: false,
      });

      if (isNotLoggedInError(error)) {
        emitter.emit(EVENT_AUTH_TOKEN_INVALID);
      }
    },
  });

  const focusQuery = usePollingQuery<SwitchChannelResult>({
    shouldThrow: true,
    query: switchChannelQuery,
    fetchPolicy: 'no-cache',
    onAfterFetch: data => {
      const {
        focusOnChannels = emptyResults,
        primaryId,
        secondaryId,
      } = cloneDeep(data.me.switchChannel);

      const forAdmin = statusRef.current.forAdmin;
      const primaryChannel = focusOnChannels.find(c => c._id === primaryId);
      const secondaryChannel = focusOnChannels.find(c => c._id === secondaryId);
      const { contents, notices, interactions, pages } = processFocusOnChannels(
        focusOnChannels,
        primaryId,
        secondaryId,
        statusRef.current.showSecondaryPages
      );

      statusRef.current.channelId = primaryId;
      statusRef.current.primaryId = primaryId;
      statusRef.current.secondaryId = secondaryId;
      statusRef.current.isSwitching = false;

      Storage.setItem(USER_CHANNELS_DATA_CONTEXT, {
        primaryId,
        secondaryId,
        forAdmin,
      }).catch(() => null);

      onFocusChannelChanged(primaryChannel, secondaryChannel);

      updateState({
        primaryChannel,
        secondaryChannel,
        primaryId,
        secondaryId,
        contents,
        notices,
        interactions,
        focusOnChannels,
        pages,
        forAdmin,
        channelsInitialized: true,
        primaryChannelInitialized: true,
        switchingChannels: false,
        isReady: true,
      });
    },
    onError: error => {
      statusRef.current.isSwitching = false;

      if (isNotLoggedInError(error)) {
        emitter.emit(EVENT_AUTH_TOKEN_INVALID);
      }
    },
  });

  async function refetchFocus() {
    // channelId is only ever set for an admin-forced channel focus (e.g. a
    // deep link). A regular member has no channelId prop, which silently
    // no-ops this on every call site (interaction create/update, pull-to-
    // refresh) instead of refreshing the currently focused channel.
    const targetChannelId = isMemberInteractionUpdateEnabled
      ? channelId ?? statusRef.current.primaryId
      : channelId;

    if (!targetChannelId) {
      return;
    }

    await focusQuery.fetch({ channelId: targetChannelId });
  }

  function dismissHomeChannelAssociationBanner() {
    updateState({ isHomeChannelAssociationBannerDismissed: true });
  }

  async function switchChannel(channelId: string, forAdmin: boolean) {
    if (statusRef.current.isSwitching) {
      return;
    }

    statusRef.current.isSwitching = true;
    statusRef.current.channelId = channelId;
    statusRef.current.forAdmin = forAdmin;

    updateState({
      primaryChannelInitialized: false,
      forAdmin,
    });

    focusQuery.stopPolling();

    await focusQuery.fetch({ channelId: statusRef.current.channelId });
  }

  useEffect(() => {
    if (!userChannels.channelsInitialized) {
      updateState({ primaryChannelInitialized: false, isReady: false });

      return;
    }

    if (
      isPostLogin &&
      statusRef.current.lastSeenDefaultChannelId !== defaultChannelIdFromUser
    ) {
      statusRef.current.lastSeenDefaultChannelId = defaultChannelIdFromUser;
      statusRef.current.homeChannelApplied = false;
    }

    const forAdmin = statusRef.current.forAdmin;
    const applied = applyHomeChannelPriority(bestDefaultChannel, statusRef.current.homeChannelApplied, forAdmin, switchChannel);

    if (applied) {
      statusRef.current.homeChannelApplied = true;

      return;
    }

    applyFallbackRouting({
      channels: userChannels.channels,
      primaryId: statusRef.current.primaryId,
      primaryChannelId: userChannels.primaryChannel?._id,
      channelId,
      forAdmin,
      bestDefaultChannel,
      switchChannel,
      updateState,
    });
  }, [
    userChannels.channels,
    userChannels.channelsInitialized,
    userChannels.primaryChannel?._id,
    channelId,
    bestDefaultChannel?._id,
    defaultChannelIdFromUser,
    isPostLogin,
  ]);

  function onLogout() {
    channelsQuery.stopPolling();
    focusQuery.stopPolling();

    updateState({
      ...defaultContext,
      primaryChannelInitialized: true,
    });

    statusRef.current.pollingStarted = false;
    statusRef.current.channelId = null;
    statusRef.current.primaryId = null;
    statusRef.current.secondaryId = null;
    statusRef.current.userInitialized = false;
    statusRef.current.homeChannelApplied = false;
    statusRef.current.lastSeenDefaultChannelId = undefined;
    isPostLoginRef.current = false;
    setIsPostLogin(false);
  }

  function startPolling() {
    if (isAuth0Enabled && !statusRef.current.pollingStarted) {
      channelsQuery.startPolling();
      statusRef.current.pollingStarted = true;
    }
  }

  useEffect(() => {
    if (!user) {
      if (statusRef.current.userInitialized) {
        onLogout();
      }

      return;
    }

    if (!isAuth0Enabled) {
      channelsQuery.startPolling();
    }

    statusRef.current.userInitialized = true;

    if (statusRef.current.storageInitialized && statusRef.current.primaryId) {
      switchChannel(statusRef.current.primaryId, statusRef.current.forAdmin);
    }

    const handleSubscriptionsChanged = () => {
      updateState({ channelsInitialized: false });
      channelsQuery.fetch();
    };

    emitter.addListener(EVENT_SUBSCRIPTIONS_CHANGED, handleSubscriptionsChanged);

    return () => {
      emitter.removeListener(EVENT_SUBSCRIPTIONS_CHANGED, handleSubscriptionsChanged);
    };
  }, [user?._id]);

  useEffect(() => {
    async function setupChannelIds() {
      try {
        const data = (await Storage.getItem(USER_CHANNELS_DATA_CONTEXT)) as Data;

        if (data) {
          updateState({
            ...data,
            storageInitialized: true,
          });

          statusRef.current.primaryId = data.primaryId;
          statusRef.current.secondaryId = data.secondaryId;
          statusRef.current.forAdmin = data.forAdmin;

          if (!statusRef.current.userInitialized || !data.primaryId) {
            return;
          }

          const hasHomeChannelPreference =
            statusRef.current.isHomeChannelSelectionEnabled &&
            defaultChannelIdFromUser != null;
          const homeChannelAlreadyApplied = statusRef.current.homeChannelApplied;

          if (!hasHomeChannelPreference || homeChannelAlreadyApplied) {
            switchChannel(data.primaryId, data.forAdmin);
          }
        } else {
          statusRef.current.storageInitialized = true;
          updateState({ storageInitialized: true });
        }
      } catch {
        statusRef.current.storageInitialized = true;
        updateState({
          storageInitialized: true,
        });
      }
    }

    emitter.addListener(EVENT_AUTH_TOKEN_INVALID, onLogout);

    const handlePostLogin = () => {
      isPostLoginRef.current = true;
      setIsPostLogin(true);
    };

    const handleAutoRevert = ({
      previousChannelName,
      currentChannelName,
    }: {
      invalidChannelId?: string;
      previousChannelName?: string;
      currentChannelName?: string;
    }) => {
      if (!isPostLoginRef.current || !previousChannelName) {
        return;
      }

      updateState({
        homeChannelAssociationData: { previousChannelName, currentChannelName },
        isHomeChannelAssociationBannerDismissed: false,
      });
    };

    emitter.addListener(EVENT_AUTH_TOKEN, handlePostLogin);
    emitter.addListener(EVENT_INVALID_HOME_CHANNEL, handleAutoRevert);

    setupChannelIds();

    statusRef.current.isMounted = true;

    instances++;

    if (instances > 1) {
      console.warn(
        'You should only have one instances of of useUserChannelsData running'
      );
    }

    return () => {
      instances--;
      emitter.removeListener(EVENT_AUTH_TOKEN, handlePostLogin);
      emitter.removeListener(EVENT_AUTH_TOKEN_INVALID, onLogout);
      emitter.removeListener(EVENT_INVALID_HOME_CHANNEL, handleAutoRevert);
    };
  }, []);

  useEffect(() => {
    setHeader(
      HEADER_PRIMARY_CHANNEL_ID,
      userChannels.primaryChannel?._id || ''
    );

    if (userChannels.primaryChannel?._id && userChannels.primaryChannel?.name) {
      analytics.setChannel(
        userChannels.primaryChannel._id,
        userChannels.primaryChannel.name
      );
    }
  }, [userChannels.primaryChannel]);

  const translatedInteractions = userChannels.interactions.map(
    (interaction: any) => {
      const interactionChannel = userChannels.channels.find(
        channel => channel._id === interaction.contentData.channel._id
      );

      return {
        ...interaction,
        contentData: translate({
          model: {
            ...interaction.contentData,
            channel: interactionChannel,
          },
          columns: ['name', 'description', 'subtitle'],
        }),
      };
    }
  );

  return {
    ...userChannels,
    interactions: translatedInteractions,
    notices: translate({
      model: userChannels?.notices,
      columns: ['name', 'description', 'subtitle'],
    }),
    contents: translate({
      model: userChannels.contents,
      columns: ['name', 'description', 'subtitle'],
    }),
    pages: translate({ model: userChannels.pages, columns: ['label'] }),
    switchChannel,
    refetch: channelsQuery.fetch,
    refetchFocus,
    startPolling,
    dismissHomeChannelAssociationBanner,
    homeChannelAssociationData: userChannels.homeChannelAssociationData,
    isHomeChannelAssociationBannerDismissed: userChannels.isHomeChannelAssociationBannerDismissed,
  };
}
